<script setup lang="ts">
/**
 * Floating editor for a dynamic block property, opened by a canvas grip — the overlay
 * can draw the grip but neither a list of choices nor an angle dial.
 *
 * Two editors, chosen by the request's kind: a list of values (DynListHandle) or the
 * angle dial (DynAngleHandle). Both open next to the grip they came from.
 */
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Facade } from "@/drawing";
import type { DynBlockPickRequest } from "@/drawing/blocks/dynPickBus";
import { Z_INDEX_MODAL } from "./layouts/constants";
import DynBlockAnglePicker from "./DynBlockAnglePicker.vue";

const request = ref<DynBlockPickRequest | null>(null);
let unsubscribe: (() => void) | null = null;

function trySubscribe(): boolean {
  if (!Facade.isInitialized) return false;
  unsubscribe = Facade.dynBlocks.onPickRequest((next) => {
    request.value = next;
  });
  return true;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  // The Facade is initialized asynchronously by the canvas view — poll until ready,
  // same pattern as SelectionMenu.
  if (!trySubscribe()) {
    pollTimer = setInterval(() => {
      if (trySubscribe() && pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    }, 100);
  }
});

onBeforeUnmount(() => {
  unsubscribe?.();
  if (pollTimer) clearInterval(pollTimer);
});

function close(): void {
  request.value = null;
}

function pick(value: string): void {
  const current = request.value;
  if (!current || !Facade.isInitialized) return close();
  if (value !== current.currentValue) {
    Facade.dynBlocks.setPropertyByHandle(
      current.blockHandle,
      current.propertyName,
      value,
    );
  }
  close();
}

// ==================== Angle dial ====================
// ODA stores angles in radians; the dial speaks degrees.

function toDegrees(radians: string): number {
  const value = Number(radians);
  return Number.isFinite(value) ? (value * 180) / Math.PI : 0;
}

function toRadians(degrees: number): string {
  return String((degrees * Math.PI) / 180);
}

/** Live step of the drag: applied, not recorded. */
function previewAngle(degrees: number): void {
  const current = request.value;
  if (!current || !Facade.isInitialized) return;
  Facade.dynBlocks.previewPropertyByHandle(
    current.blockHandle,
    current.propertyName,
    toRadians(degrees),
  );
}

/** End of the drag: the whole gesture becomes one undo step. */
function commitAngle(degrees: number): void {
  const current = request.value;
  if (!current || !Facade.isInitialized) return;
  Facade.dynBlocks.commitPropertyByHandle(
    current.blockHandle,
    current.propertyName,
    current.currentValue,
    toRadians(degrees),
  );
}
</script>

<template>
  <DynBlockAnglePicker
    v-if="request?.kind === 'angle'"
    :degrees="toDegrees(request.currentValue)"
    :label="request.propertyName"
    :anchor="request.screenPosition"
    @preview="previewAngle"
    @commit="commitAngle"
    @close="close"
  />

  <!-- Backdrop: any click outside dismisses the picker without changing anything. -->
  <div
    v-else-if="request"
    class="picker-backdrop"
    :style="{ zIndex: Z_INDEX_MODAL }"
    @pointerdown.self="close"
  >
    <div
      class="picker canvas-dropdown"
      :style="{
        left: `${request.screenPosition.x}px`,
        top: `${request.screenPosition.y}px`,
      }"
    >
      <h6 class="picker-header">{{ request.propertyName }}</h6>
      <button
        v-for="value in request.allowedValues"
        :key="value"
        type="button"
        class="picker-item"
        :class="{ active: value === request.currentValue }"
        @click="() => pick(value)"
      >
        {{ value }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.picker-backdrop {
  position: absolute;
  inset: 0;
}

.picker {
  position: absolute;
  // Open to the right of the handle, vertically centered on it.
  transform: translate(12px, -50%);
  min-width: 120px;
  max-height: 45vh;
  overflow-y: auto;
  padding: 4px 0;
  border-radius: 6px;
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.picker-header {
  margin: 0;
  padding: 4px 12px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
}

.picker-item {
  display: block;
  width: 100%;
  padding: 6px 12px;
  border: 0;
  background: transparent;
  color: white;
  text-align: left;
  font-size: 0.875rem;

  &:hover,
  &:focus {
    background-color: rgba(255, 255, 255, 0.08);
  }

  &.active {
    background-color: var(--canvas-btn-active-bg);
  }
}
</style>
